import React, { createContext, useContext } from "react";
import { UserMetadata, type Session, type User } from "@supabase/supabase-js";
import { OrganizationMemberMetadata, OrganizationWithMembers } from "@/types/supabase.ext";
import { useAuthState } from "@/hooks/useAuthState";
import { useAuthMethods } from "@/hooks/useAuthMethods";

interface AuthContextType {
  session: Session | null;
  user: User | null;
  userMetadata: OrganizationMemberMetadata | null;
  organization: OrganizationWithMembers | null;
  isLoading: boolean;
  signIn: (
    email: string,
    password: string,
  ) => Promise<{
    success: boolean;
    error: string | null;
  }>;
  signOut: () => Promise<void>;
  signUp: (
    email: string,
    password: string,
    metadata?: UserMetadata,
  ) => Promise<{
    success: boolean;
    error: string | null;
  }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const { session, user, userMetadata, organization, isLoading } = useAuthState();
  const { signIn, signUp, signOut } = useAuthMethods();

  const value = {
    session,
    user,
    userMetadata,
    organization,
    isLoading,
    signIn,
    signUp,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
